/**
 * Weight Freeze (v2) — Initiate: the candidate row model.
 *
 * A weight freeze is agreed per single-SKU shipment, so there is one
 * population here and one row shape: the table, the selection bar and the
 * request drawer all read the same fields.
 *
 * Rows are ordered by variance, highest first, because a SKU that always
 * bills at one weight has nothing to gain from a freeze.
 *
 * This module is pure and evidence-agnostic: every function that needs to
 * know sorter/merchant counts takes a pre-built `evIndex` (a
 * `Map<rowKey, {sorter, merchant, none, partial, both, ...}>`) as a
 * parameter. The one, real rollup lives in `../state/dwsEvidence.js`
 * (`buildEvidenceIndex(rows, uploads)`) — this file never recomputes it and
 * never defines a competing version.
 */

/** A row's variance at or above this share is what the "Variance 10% or more" filter means. */
export const VARIANCE_THRESHOLD = 10

/** @param {object} entity a SKU from SKUS */
export function toRow(entity) {
  const ships = entity.ships || 0
  const sorterCount = entity.sorterCount || 0
  return {
    key: entity.sku,
    label: entity.sku,
    sub: entity.sub,
    ships,
    sorterCount,
    merchantCount: entity.merchantCount || 0,
    hist: entity.hist || [],
    mpw: entity.mpw,
    spread: entity.spread,
    deadKg: entity.deadKg,
    volKg: entity.volKg,
    packId: entity.packId,
  }
}

/** Variance desc, shipments desc as the tie-break. Returns a new array. */
export function rankRows(rows) {
  return rows.slice().sort((a, b) => b.spread - a.spread || b.ships - a.ships)
}

export const FILTERS = [
  {id: 'all', label: 'All rows'},
  {id: 'vary', label: 'Variance 10% or more'},
  {id: 'ready', label: 'Ready to freeze'},
  {id: 'blocked', label: 'DWS data not found'},
  {id: 'dws', label: 'Has sorter images'},
  {id: 'nodws', label: 'No sorter images'},
]

/**
 * @param {Array} rows
 * @param {{q: string, filter: string}} query
 * @param {Map<string, object>} evIndex from buildEvidenceIndex — never recomputed here
 */
export function filterRows(rows, {q, filter}, evIndex) {
  const t = String(q || '').trim().toLowerCase()
  return rows.filter((r) => {
    if (t && r.label.toLowerCase().indexOf(t) < 0 && String(r.sub || '').toLowerCase().indexOf(t) < 0) return false
    const ev = evIndex.get(r.key) || {sorter: 0, merchant: 0, both: false}
    if (filter === 'dws') return r.sorterCount > 0
    if (filter === 'nodws') return r.sorterCount === 0
    // The blocking condition, and the ONLY one: merchant evidence.
    if (filter === 'blocked') return ev.merchant === 0
    if (filter === 'ready') return ev.both
    if (filter === 'vary') return r.spread >= VARIANCE_THRESHOLD
    return true
  })
}

/** Counted over every candidate row, deliberately ignoring the search and the filter. */
export function tabStats(rows, evIndex) {
  let vary = 0
  let nodws = 0
  let blocked = 0
  let ready = 0
  rows.forEach((r) => {
    const ev = evIndex.get(r.key) || {sorter: 0, merchant: 0, both: false}
    if (r.spread >= VARIANCE_THRESHOLD) vary += 1
    if (!r.sorterCount) nodws += 1
    if (!ev.merchant) blocked += 1
    if (ev.both) ready += 1
  })
  return {vary, nodws, blocked, ready}
}

/**
 * Resolved against the FULL ranked list, not the filtered view. A merchant
 * who ticks a row and then narrows the filter has not deselected it — the
 * filter is a viewing aid. Resolving against the visible rows instead would
 * silently drop rows out of a request at submit time.
 */
export function pickedRows(allRows, selection) {
  return allRows.filter((r) => selection.has(r.key))
}

/** The modal weight and how many shipments sit off it, straight from hist. */
export function modalFacts(hist, mpw) {
  const h = hist || []
  let total = 0
  let best = null
  h.forEach((x) => {
    total += x[1]
    if (!best || x[1] > best[1]) best = x
  })
  return {total, modal: best ? best[0] : mpw, off: total - (best ? best[1] : 0), bands: h.length}
}

/**
 * The gate list's three-way state. MERCHANT-FIRST and deliberately not the
 * rollup's own `none`/`partial`/`both` triple: a row the courier has scanned
 * a hundred times but the merchant has never filmed reads 'none' here,
 * because from the freeze's point of view there is nothing to argue from.
 */
export function gateState(ev) {
  if (!ev.merchant) return 'none'
  if (!ev.sorter) return 'partial'
  return 'both'
}

const ACCESSORS = {
  label: (r) => r.label,
  ships: (r) => r.ships,
  dead: (r) => r.deadKg,
  mpw: (r) => r.mpw,
  spread: (r) => r.spread,
}

/**
 * `null` sort means "leave it in rank order" — the section's whole premise is
 * that the default order is already the useful one. Every key reads a field the
 * row already carries, so no comparator ever recomputes a rollup — which would
 * run it O(n log n) times.
 */
export function sortRows(rows, sort) {
  if (!sort || !sort.key) return rows
  const get = ACCESSORS[sort.key]
  if (!get) return rows
  const sign = sort.dir === 'asc' ? 1 : -1
  return rows.slice().sort((a, b) => {
    const x = get(a)
    const y = get(b)
    if (typeof x === 'string' || typeof y === 'string') return sign * String(x).localeCompare(String(y))
    return sign * (x - y)
  })
}

/** DESC on the first click of a column, ASC on the second. MUI's default is the other way round. */
export function nextSort(sort, key) {
  if (sort && sort.key === key && sort.dir === 'desc') return {key, dir: 'asc'}
  return {key, dir: 'desc'}
}
